import { useState } from 'react';
import { Tick, ArrowDown2, Cross } from '@/components/icons';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export const FACET_ALL_VALUE = 'all';

const SEARCH_THRESHOLD = 7;

export interface FacetSelectOption {
    value: string;
    label: string;
    count?: number;
}

interface FacetSelectProps {
    label: string;
    options: FacetSelectOption[];
    value: string;
    onChange: (value: string) => void;
    allLabel?: string;
    totalCount?: number;
    searchPlaceholder?: string;
    emptyMessage?: string;
    className?: string;
}

function FacetCount({ count }: { count?: number }) {
    if (typeof count !== 'number') return null;

    return <span className="ml-auto pl-3 text-xs text-muted-foreground">{count}</span>;
}

export default function FacetSelect({
    label,
    options,
    value,
    onChange,
    allLabel = 'Todos',
    totalCount,
    searchPlaceholder,
    emptyMessage = 'Nenhuma opção encontrada.',
    className,
}: FacetSelectProps) {
    const [open, setOpen] = useState(false);

    const isFiltered = value !== FACET_ALL_VALUE;
    const selectedLabel = isFiltered
        ? (options.find((option) => option.value === value)?.label ?? value)
        : allLabel;

    const handleSelect = (nextValue: string) => {
        onChange(nextValue);
        setOpen(false);
    };

    return (
        <div className={cn('flex shrink-0 items-center gap-1', className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className={cn(
                            'max-w-64 justify-between gap-2 font-normal',
                            isFiltered
                                ? 'border-brand/30 bg-brand-subtle text-brand hover:bg-brand-subtle hover:text-brand'
                                : 'text-muted-foreground',
                        )}
                    >
                        <span className="truncate">
                            {label}: <span className="font-medium">{selectedLabel}</span>
                        </span>
                        <ArrowDown2 className="size-4 shrink-0 opacity-60" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-64 p-0">
                    <Command>
                        {options.length > SEARCH_THRESHOLD && (
                            <CommandInput
                                placeholder={
                                    searchPlaceholder ?? `Filtrar ${label.toLowerCase()}...`
                                }
                            />
                        )}
                        <CommandList className="max-h-64">
                            <CommandEmpty>{emptyMessage}</CommandEmpty>
                            <CommandGroup>
                                <CommandItem
                                    value={FACET_ALL_VALUE}
                                    onSelect={() => handleSelect(FACET_ALL_VALUE)}
                                >
                                    <Tick
                                        className={cn(
                                            'size-4 shrink-0',
                                            isFiltered ? 'opacity-0' : 'opacity-100',
                                        )}
                                    />
                                    <span className="truncate">{allLabel}</span>
                                    <FacetCount count={totalCount} />
                                </CommandItem>
                                {options.map((option) => (
                                    <CommandItem
                                        key={option.value}
                                        value={option.label}
                                        onSelect={() => handleSelect(option.value)}
                                    >
                                        <Tick
                                            className={cn(
                                                'size-4 shrink-0',
                                                option.value === value
                                                    ? 'opacity-100'
                                                    : 'opacity-0',
                                            )}
                                        />
                                        <span className="truncate">{option.label}</span>
                                        <FacetCount count={option.count} />
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            {isFiltered && (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
                    aria-label={`Limpar filtro de ${label.toLowerCase()}`}
                    onClick={() => onChange(FACET_ALL_VALUE)}
                >
                    <Cross className="size-4" />
                </Button>
            )}
        </div>
    );
}
